import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, initials } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useRoles, labelForRole } from "../lib/roles";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound, X, Loader2, Users2, ExternalLink } from "lucide-react";

export default function Users() {
  const { user: me } = useAuth();
  const isSuper = me?.role === "super_admin";
  const roles = useRoles();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode: 'create'|'edit'|'reset', user?: {}}

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/users"); setUsers(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.full_name} (${u.email}) ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de suppression");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Administration</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Utilisateurs</h1>
          <p className="text-sm text-slate-500 mt-1">{users.length} utilisateur(s) — {isSuper ? "vous pouvez créer, modifier ou supprimer des comptes" : "lecture seule"}</p>
        </div>
        {isSuper && (
          <button
            onClick={() => setModal({ mode: "create" })}
            className="h-10 px-4 bg-btp-terra hover:bg-btp-terra-hover text-white rounded-md text-sm font-semibold flex items-center gap-2"
            data-testid="user-create-button">
            <Plus size={16} /> Nouvel utilisateur
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm flex flex-col items-center gap-2"><Users2 size={28} /> Aucun utilisateur</div>
        ) : (
          <table className="btp-table">
            <thead><tr><th></th><th>Nom</th><th>Email</th><th>Rôle</th><th>Expertise</th><th>Inscrit le</th>{isSuper && <th className="text-right">Actions</th>}</tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td><div className="w-8 h-8 rounded-full bg-btp-navy text-white flex items-center justify-center text-[11px] font-semibold">{initials(u.full_name)}</div></td>
                  <td className="font-medium">{u.full_name}{u.id === me?.id && <span className="ml-2 text-[10px] uppercase text-btp-terra font-semibold">vous</span>}</td>
                  <td className="text-xs font-mono text-slate-500">{u.email}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded uppercase tracking-wider font-semibold ${
                      u.role === "super_admin" ? "bg-btp-terra/15 text-btp-terra" :
                      u.role === "admin" ? "bg-btp-navy/10 text-btp-navy" : "bg-slate-100 text-slate-600"
                    }`}>{labelForRole(u.role, roles)}</span>
                  </td>
                  <td className="text-sm">{u.expertise || "—"}</td>
                  <td className="text-xs font-mono text-slate-500">{fmtDate(u.created_at)}</td>
                  {isSuper && (
                    <td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link to={`/users/${u.id}/profile`} title="Voir profil" className="p-1.5 hover:bg-slate-100 rounded text-slate-600" data-testid={`user-profile-${u.id}`}><ExternalLink size={14} /></Link>
                        <button onClick={() => setModal({ mode: "edit", user: u })} title="Modifier" className="p-1.5 hover:bg-slate-100 rounded text-slate-600" data-testid={`user-edit-${u.id}`}><Pencil size={14} /></button>
                        <button onClick={() => setModal({ mode: "reset", user: u })} title="Réinitialiser mot de passe" className="p-1.5 hover:bg-slate-100 rounded text-slate-600" data-testid={`user-reset-${u.id}`}><KeyRound size={14} /></button>
                        <button onClick={() => onDelete(u)} title="Supprimer" disabled={u.id === me?.id} className="p-1.5 hover:bg-red-50 rounded text-red-600 disabled:opacity-30" data-testid={`user-delete-${u.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <UserModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function UserModal({ modal, onClose, onSaved }) {
  const isCreate = modal.mode === "create";
  const isEdit = modal.mode === "edit";
  const isReset = modal.mode === "reset";
  const u = modal.user;
  const roles = useRoles();

  const [form, setForm] = useState(
    isCreate
      ? { email: "", password: "", full_name: "", role: "expert", expertise: "" }
      : isEdit
      ? { full_name: u.full_name || "", role: u.role, expertise: u.expertise || "" }
      : { new_password: "" }
  );
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isCreate) {
        await api.post("/users", form);
        toast.success("Utilisateur créé");
      } else if (isEdit) {
        await api.patch(`/users/${u.id}`, form);
        toast.success("Utilisateur modifié");
      } else {
        await api.post(`/users/${u.id}/reset-password`, { new_password: form.new_password });
        toast.success("Mot de passe réinitialisé");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const title = isCreate ? "Nouvel utilisateur" : isEdit ? `Modifier ${u.full_name}` : `Réinitialiser le mot de passe`;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md border border-slate-200 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="font-heading font-bold text-base">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="user-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {isCreate && (
            <>
              <Field label="Nom complet"><input required value={form.full_name} onChange={set("full_name")} className="input" data-testid="user-modal-name" /></Field>
              <Field label="Email"><input type="email" required value={form.email} onChange={set("email")} className="input" data-testid="user-modal-email" /></Field>
              <Field label="Mot de passe initial"><input type="password" required minLength={6} value={form.password} onChange={set("password")} className="input" data-testid="user-modal-password" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Rôle">
                  <select value={form.role} onChange={set("role")} className="input" data-testid="user-modal-role">
                    {roles.map((r) => <option key={r.key} value={r.key}>{r.label}{!r.builtin ? " ✦" : ""}</option>)}
                  </select>
                </Field>
                <Field label="Expertise"><input value={form.expertise} onChange={set("expertise")} placeholder="Géotechnicien…" className="input" data-testid="user-modal-expertise" /></Field>
              </div>
            </>
          )}
          {isEdit && (
            <>
              <Field label="Nom complet"><input required value={form.full_name} onChange={set("full_name")} className="input" data-testid="user-modal-name" /></Field>
              <Field label="Rôle">
                <select value={form.role} onChange={set("role")} className="input" data-testid="user-modal-role">
                  {roles.map((r) => <option key={r.key} value={r.key}>{r.label}{!r.builtin ? " ✦" : ""}</option>)}
                </select>
              </Field>
              <Field label="Expertise"><input value={form.expertise} onChange={set("expertise")} placeholder="Géotechnicien…" className="input" data-testid="user-modal-expertise" /></Field>
            </>
          )}
          {isReset && (
            <Field label="Nouveau mot de passe">
              <input type="password" required minLength={6} value={form.new_password} onChange={set("new_password")} className="input" data-testid="user-modal-new-password" />
            </Field>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-10 px-4 border border-slate-200 hover:bg-slate-50 rounded-md text-sm">Annuler</button>
            <button type="submit" disabled={saving} className="h-10 px-4 bg-btp-terra hover:bg-btp-terra-hover disabled:opacity-60 text-white rounded-md text-sm font-semibold flex items-center gap-2" data-testid="user-modal-submit">
              {saving && <Loader2 size={14} className="animate-spin" />} {isCreate ? "Créer" : isEdit ? "Enregistrer" : "Réinitialiser"}
            </button>
          </div>
        </form>
      </div>
      <style>{`.input{ margin-top:0.375rem; width:100%; height:2.5rem; padding:0 0.75rem; border:1px solid #cbd5e1; border-radius:0.375rem; font-size:0.875rem; } .input:focus{ outline:none; border-color:#1e3a5f; box-shadow:0 0 0 1px #1e3a5f; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</label>
      {children}
    </div>
  );
}
